import {TariffsRepository} from '@yandex-delivery/api/repo/tariffs'
import {get} from '@yandex-delivery/utils'

import {toCargoInfoBody, toTariffsCargoInfo} from './mappers/toTariffsCargoInfo'
import {toTariffData, toTariffsList, toTariffsListBody} from './mappers/toTariffsList'
import {toNddInfoBody, toTariffsNddInfo} from './mappers/toTariffsNddInfo'
import {toTariffsZonesInfo} from './mappers/toTariffsZonesInfo'
import {ServiceConfig, TariffsService} from './types'
import {downloadFile} from './utils'
import {validateTariffs} from './validators/validateTariffs'

const getBody = get('body')

export function createTariffsService(repo: TariffsRepository, config: ServiceConfig): TariffsService {
  const {fetchTariffsZones, fetchTariffsList, fetchCargoInfo, fetchNddInfo, fetchCargoExcel, fetchNddExcel} = repo
  const {hostname: domain, shouldUseCountryParam} = config

  const getDefaultParams = (country: string) => ({country: shouldUseCountryParam ? country : undefined})

  return {
    fetchTariffsZones() {
      const params = {domain}

      return fetchTariffsZones({params, cacheKey: 'zones'}).then(getBody).then(toTariffsZonesInfo)
    },

    fetchTariffsList(country, zone, tariff) {
      const params = getDefaultParams(country)
      const body = toTariffsListBody(zone)
      const cacheKey = ['tariffs/list', country, zone].join('-')

      return fetchTariffsList({params, body, cacheKey})
        .then(getBody)
        .then(validateTariffs(tariff))
        .then(toTariffData(tariff))
    },

    fetchCargoInfo(country, zone, tariff, filters) {
      const params = getDefaultParams(country)
      const body = toCargoInfoBody(zone, tariff, filters)
      const cacheKey = JSON.stringify(['tariffs/cargo-info', country, zone, tariff, filters])

      return fetchCargoInfo({params, body, cacheKey}).then(getBody).then(toTariffsCargoInfo)
    },

    fetchNddInfo(country, zone, tariff) {
      const params = getDefaultParams(country)
      const body = toNddInfoBody(zone, tariff)
      const cacheKey = ['tariffs/ndd-info', country, zone, tariff].join('-')

      return fetchNddInfo({params, body, cacheKey}).then(getBody).then(toTariffsNddInfo)
    },

    fetchNddExcel(country, tariff) {
      const params = getDefaultParams(country)
      const body = {tariff}
      const headers = {Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
      const cacheKey = ['tariffs/ndd-excel', country, tariff].join('-')

      return fetchNddExcel({params, body, headers, cacheKey})
        .then(downloadFile('tariffs.xlsx'))
        .then(() => true)
    },

    fetchCargoExcel(country, zone, tariff) {
      const params = getDefaultParams(country)
      const body = {tariff, zone_name: zone}
      const headers = {Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
      const cacheKey = ['tariffs/cargo-excel', country, zone, tariff].join('-')

      return fetchCargoExcel({params, body, headers, cacheKey})
        .then(downloadFile('tariffs.xlsx'))
        .then(() => true)
    },

    _fetchTariffsList(country, zone) {
      const params = getDefaultParams(country)
      const body = toTariffsListBody(zone)
      const cacheKey = ['tariffs/list', zone].join('-')

      return fetchTariffsList({params, body, cacheKey}).then(getBody)
    },

    getTariffsList(country, zone) {
      return this._fetchTariffsList(country, zone).then(toTariffsList)
    },
  }
}

export * from './types'
